import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, LayoutGrid } from 'lucide-react';
import EvaluationReport from '../../../components/lld/EvaluationReport';
import { lldEvaluationService, EvaluationReport as EvaluationReportData } from '../../../services/lldEvaluationService';
import { lldProgressService } from '../../../services/lldProgressService';

/**
 * Results screen shown after submitting an LLD practice attempt.
 * Loads the persisted evaluation for the problem and renders the report,
 * including the "scoring not available" degradation for custom problems.
 */
const EvaluationResults: React.FC = () => {
 const { problemId } = useParams<{ problemId: string }>();
 const navigate = useNavigate();

 const [report, setReport] = useState<EvaluationReportData | null>(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [markingComplete, setMarkingComplete] = useState(false);
 const [isCompleted, setIsCompleted] = useState(false);

 useEffect(() => {
  let active = true;
  if (!problemId) return;
  setLoading(true);
  setError(null);
  lldEvaluationService
   .getEvaluation(problemId)
   .then((data) => {
    if (!active) return;
    setReport(data);
    if (!data) setError('No evaluation found for this problem yet.');
   })
   .catch(() => {
    if (active) setError('Could not load your evaluation. Please try again.');
   })
   .finally(() => {
    if (active) setLoading(false);
   });
  return () => {
   active = false;
  };
 }, [problemId]);

 const handleMarkComplete = useCallback(async () => {
  if (!problemId || !report) return;
  setMarkingComplete(true);
  try {
   await lldProgressService.saveProgress(problemId, {
    mode: report.mode,
    isCompleted: true,
   });
   setIsCompleted(true);
  } catch {
   // keep the button enabled so the user can retry
  } finally {
   setMarkingComplete(false);
  }
 }, [problemId, report]);

 const goToCatalog = useCallback(() => navigate('/practice/lld'), [navigate]);
 const retry = useCallback(() => navigate(`/practice/lld/${problemId}/mode`), [navigate, problemId]);

 if (loading) {
  return (
   <div className="min-h-[calc(100vh-4rem)] mt-16 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
     <Loader2 className="w-5 h-5 animate-spin" />
     Loading your evaluation…
    </div>
   </div>
  );
 }

 if (error || !report) {
  return (
   <div className="min-h-[calc(100vh-4rem)] mt-16 flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
    <div className="max-w-md w-full text-center rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8">
     <AlertCircle className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
     <h1 className="text-lg font-semibold text-gray-900 dark:text-white">No evaluation to show</h1>
     <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
      {error || 'Submit a practice attempt to generate an evaluation.'}
     </p>
     <button
      onClick={goToCatalog}
      className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
     >
      <LayoutGrid className="w-4 h-4" />
      Back to Catalog
     </button>
    </div>
   </div>
  );
 }

 return (
  <div className="lld-page lld-evaluation-page mt-16">
   <EvaluationReport
    evaluation={report}
    problemId={problemId || ''}
    onRetry={retry}
    onNextProblem={goToCatalog}
    onBackToCatalog={goToCatalog}
    onMarkComplete={handleMarkComplete}
    markingComplete={markingComplete}
    isCompleted={isCompleted}
   />
  </div>
 );
};

export default EvaluationResults;
